import { useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CarrierRatingSheet, monthLabel } from '@/components/reports/CarrierRatingSheet';
import { MeetingBrief } from '@/components/reports/MeetingBrief';
import { carriers, getCarrier } from '@/data/arrivalsData';
import { carrierMonthly, defaultRatingMonth, ratingMonths } from '@/data/carrierMonthly';
import { t } from '@/i18n';
import { ArrowLeft, Printer } from 'lucide-react';

// Monthly rating of one carrier as a page of its own, without the app layout,
// so it prints cleanly. Carrier in the path, month in ?mesic=YYYY-MM.
export default function CarrierRating() {
  const { carrierId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const carrier = getCarrier(carrierId) || carriers[0];
  const month = params.get('mesic') || defaultRatingMonth();
  const rating = useMemo(() => carrierMonthly(carrier.id, month), [carrier.id, month]);
  const go = (id, m) => navigate(`/statistiky/dopravci/${id}?mesic=${m}`, { replace: true });

  return (
    <div className="min-h-screen bg-background print:bg-white">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10 space-y-4 print:p-0 print:max-w-none">
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <Button variant="ghost" size="sm" className="gap-1" asChild>
            <Link to="/statistiky/dopravci"><ArrowLeft className="h-4 w-4" /> {t('rating.back')}</Link>
          </Button>
          <div className="ml-auto flex flex-wrap gap-2">
            <Select value={carrier.id} onValueChange={id => go(id, month)}>
              <SelectTrigger className="w-56 h-9" aria-label={t('stats.carrier.name')}><SelectValue /></SelectTrigger>
              <SelectContent>
                {carriers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={month} onValueChange={m => go(carrier.id, m)}>
              <SelectTrigger className="w-44 h-9" aria-label={t('rating.month')}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ratingMonths().map(m => (
                  <SelectItem key={m.month} value={m.month}>
                    <span className="capitalize">{monthLabel(m.month)}</span>{!m.complete && ` (${t('rating.partial')})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="gap-1 h-9" onClick={() => window.print()}>
              <Printer className="h-4 w-4" /> {t('rating.print')}
            </Button>
          </div>
        </div>
        <CarrierRatingSheet carrier={carrier} rating={rating} />
        <MeetingBrief key={`${carrier.id}-${month}`} carrier={carrier} rating={rating} />
      </div>
    </div>
  );
}
